import {useEffect, useState} from 'react'

import {posthogTrack} from 'src/utils'

interface IStepState {
	isInViewport: boolean
	isStarted: boolean
	activations: Set<'inViewport' | 'isNotInViewport' | 'finished'>
}

const THRESHOLD = 0.1

const getInViewport = (element: HTMLDivElement) => {
	const rect = element.getBoundingClientRect()
	return (
		(rect.y >= 0 && rect.y <= (window.innerHeight || document.documentElement.clientHeight)) ||
		(rect.bottom >= 0 &&
			rect.bottom <= (window.innerHeight || document.documentElement.clientHeight))
	)
}

export default function useStepsForAnalytics(refsSteps: React.MutableRefObject<HTMLDivElement[]>) {
	const [, setStepsStates] = useState<Record<string, IStepState>>({})

	useEffect(() => {
		const newStepsStates = refsSteps.current.reduce<Record<string, IStepState>>((prev, curr) => {
			if (curr && curr.dataset.step !== undefined) {
				prev[curr.dataset.step] = {
					isInViewport: false,
					isStarted: false,
					activations: new Set(),
				}
			}
			return prev
		}, {})

		setStepsStates(newStepsStates)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setTimeout(() => {
			const newStepsStates = refsSteps.current.reduce<Record<string, IStepState>>((prev, curr) => {
				if (curr && curr.dataset.step !== undefined) {
					const isInViewport = getInViewport(curr)
					prev[curr.dataset.step] = {
						isInViewport,
						isStarted: false,
						activations: new Set(),
					}
				}
				return prev
			}, {})

			setStepsStates(newStepsStates)
		}, 500)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		const observerCb: IntersectionObserverCallback = ([e]) => {
			const step = (e.target as HTMLDivElement).dataset.step
			let isInViewport = false

			if (e.isIntersecting && e.intersectionRatio >= THRESHOLD) {
				isInViewport = true
			} else if (e.intersectionRatio <= THRESHOLD) {
				isInViewport = false
			}

			if (step) {
				if (step === 'finish') {
					setStepsStates((v) => {
						if (v['6'].isStarted) {
							posthogTrack(`[PAYSTUB] Step 6 finished`)
						}

						return {
							...v,
							'6': {...v['6'], isStarted: false},
							[step]: {...v[step], isStarted: false, isInViewport},
						}
					})
					return
				}

				setStepsStates((v) => {
					const isFinished = !isInViewport && v[step].isStarted
					const isInViewportActivated = v[step].activations.has('inViewport')
					const isNotViewportActivated = v[step].activations.has('isNotInViewport')

					if (isFinished) {
						posthogTrack(`[PAYSTUB] Step ${step} finished`)
					} else if (isInViewport && !isInViewportActivated) {
						posthogTrack(`[PAYSTUB] Step ${step} is in the viewport`)
					} else if (!isInViewport && !isNotViewportActivated) {
						posthogTrack(`[PAYSTUB] Step ${step} is not in the viewport`)
					}

					const newActivations = new Set(v[step].activations)
					if (isInViewport) {
						newActivations.add('inViewport')
					} else if (isFinished) {
						newActivations.add('finished')
					} else {
						newActivations.add('isNotInViewport')
					}

					return {
						...v,
						[step]: {
							...v[step],
							isStarted: false,
							isInViewport,
							activations: newActivations,
						},
					}
				})
			}
		}
		const observerForStep = new IntersectionObserver(observerCb, {threshold: THRESHOLD})

		refsSteps.current.map((step) => {
			observerForStep.observe(step)
		})

		const refs = refsSteps

		return () => {
			refs.current.map((step) => {
				if (step) observerForStep.unobserve(step)
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {setStepsStates}
}
